import dayjs from 'dayjs'

/** `enforcement.id` of a suspension with no fixed end ("Indefinitely"). */
const INDEFINITE_ENFORCEMENT_ID = 3

/** A suspension as it arrives on a roster player from the API. */
export interface PlayerSuspensionSource {
  is_active?: boolean | null
  start_date?: string | null
  end_date?: string | null
  enforcement?: { id?: number | string | null } | null
}

export interface PlayerStatusSource {
  is_injured?: boolean | null
  suspensions?: PlayerSuspensionSource[] | null
}

export interface SuspensionStatus {
  kind: 'suspended' | 'suspensionEnded'
  startDate: string | null
  endDate: string | null
  /** No fixed end: the indefinite enforcement, or no end date at all. */
  isIndefinite: boolean
}

export type PlayerStatus = SuspensionStatus | { kind: 'injured' }

/**
 * Start date as a sortable number; a missing start sorts last. Only the order
 * matters here, and every date parses the same way, so no zone is involved.
 */
const startTime = (suspension: PlayerSuspensionSource): number => {
  const start = suspension.start_date ? dayjs(suspension.start_date) : null
  return start?.isValid() ? start.valueOf() : Number.POSITIVE_INFINITY
}

/**
 * The earliest suspension by start date — the one the Team screen's roster
 * table has always shown.
 */
const earliestSuspension = (
  suspensions: PlayerSuspensionSource[]
): PlayerSuspensionSource | undefined =>
  [...suspensions].sort((a, b) => startTime(a) - startTime(b))[0]

const toSuspensionStatus = (
  suspension: PlayerSuspensionSource
): SuspensionStatus => ({
  kind: suspension.is_active ? 'suspended' : 'suspensionEnded',
  startDate: suspension.start_date ?? null,
  endDate: suspension.end_date ?? null,
  isIndefinite:
    Number(suspension.enforcement?.id) === INDEFINITE_ENFORCEMENT_ID ||
    !suspension.end_date,
})

/**
 * What a roster player's Status column shows, shared by the Team screen's
 * roster table and the competition Operations roster grid.
 *
 * The earliest suspension decides, as it always has on the Team screen — but
 * by its own `is_active` now: the old check built a list of active suspensions
 * and never used it, so an expired suspension read "Suspended" forever.
 *
 * Precedence: an active suspension, then an injury, then a suspension that has
 * ended — a current injury matters more than past history.
 *
 * `null` when there is nothing to show.
 */
export const toPlayerStatus = (
  player: PlayerStatusSource
): PlayerStatus | null => {
  const suspensions = Array.isArray(player.suspensions)
    ? player.suspensions
    : []
  const suspension = earliestSuspension(suspensions)
  const suspensionStatus = suspension ? toSuspensionStatus(suspension) : null

  if (suspensionStatus?.kind === 'suspended') {
    return suspensionStatus
  }
  if (player.is_injured) {
    return { kind: 'injured' }
  }
  return suspensionStatus
}
